import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Select360 } from '../kit/Select360';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Settings2, SlidersHorizontal, X } from 'lucide-react';
import { useIsMobile } from '../../hooks/useMobileLayout';
import { haptic } from '../../utils/haptics';
import { useLocation, useSearchParams } from 'react-router-dom';
import { PeriodNav, usePeriodParams } from '../finance/PeriodNav';
import { MomentsDialog } from '../finance/MomentsDialog';
import { useTenant } from '../../contexts/TenantContext';
import { getApiUrl } from '../../utils/api';
import './filters.css';

// 02.7 · Un seul module de filtres (période · comparaison · point de vente · moment) pour toutes les pages de données.
// Les filtres vivent dans l'adresse (plage, mois, jour, semaine, debut, fin, pdv, service) et sont repris d'une page à l'autre (sessionStorage).
export const FILTER_KEYS = ['plage', 'mois', 'jour', 'semaine', 'debut', 'fin', 'pdv', 'service'];
const STORE = 'filters:last';
const NA_TITLE = 'Ne s’applique pas à cette page';

export const useMoments = () => {
  const [moments, setMoments] = useState([]);
  const [lieux, setLieux] = useState([]);
  const load = () => {
    axios.get(`${getApiUrl()}/finance-hub/moments`, { withCredentials: true }).then((r) => setMoments(r.data || [])).catch(() => setMoments([]));
    axios.get(`${getApiUrl()}/lieux`, { withCredentials: true }).then((r) => setLieux(r.data || [])).catch(() => setLieux([]));
  };
  useEffect(() => { load(); const h = () => load(); window.addEventListener('moments:updated', h); return () => window.removeEventListener('moments:updated', h); }, []);
  return { moments, lieux, reload: load };
};

/** Filtres actifs lus dans l'adresse + prédicat « ce moment est dans le périmètre » (point de vente × moment de service). */
export const useAxes = () => {
  const [params] = useSearchParams();
  const pdv = params.get('pdv') || null;
  const service = params.get('service') || null;
  const legacy = params.get('moment') || null;
  const keep = (m) => (legacy ? m.moment_id === legacy : (!pdv || m.lieu_id === pdv) && (!service || (m.service || '') === service));
  return { pdv, service, legacy, active: !!(pdv || service || legacy), keep };
};

/** Bornes [début, fin] de la période active (AAAA-MM-JJ) + type, pour les pages qui filtrent leurs données. */
export const usePeriodBounds = () => { const { tenant } = useTenant(); const pp = usePeriodParams(tenant?.business_day_start); return { bounds: pp.bounds, kind: pp.kind, label: pp.label, compared: pp.compared }; };

export const useFiltersPersistence = () => {
  const [params, setParams] = useSearchParams();
  const { pathname } = useLocation();
  const restored = useRef(null);
  useEffect(() => {
    // Reprise des derniers filtres une seule fois à l'arrivée sur la page (pas quand l'utilisateur vide un filtre)
    if (restored.current !== pathname) {
      restored.current = pathname;
      if (!FILTER_KEYS.some((k) => params.has(k))) {
        try {
          const saved = JSON.parse(sessionStorage.getItem(STORE) || 'null');
          if (saved && Object.keys(saved).length) { const p = new URLSearchParams(params); Object.entries(saved).forEach(([k, v]) => p.set(k, v)); setParams(p, { replace: true }); return; }
        } catch { /* ignore */ }
      }
    }
    const obj = {}; FILTER_KEYS.forEach((k) => { if (params.has(k)) obj[k] = params.get(k); });
    sessionStorage.setItem(STORE, JSON.stringify(obj));
  }, [params, pathname, setParams]);
};

const Select = ({ label, value, onChange, options, na, testId }) => (
  <label className={`flt-field ${na ? 'is-na' : ''}`} title={na ? NA_TITLE : undefined} data-testid={`${testId}-wrap`}>
    <span>{label}</span>
    <Select360 className="fin-select" value={na ? '' : value || ''} disabled={na || !options.length} onChange={(e) => onChange(e.target.value || null)} aria-label={na ? `${label} · ${NA_TITLE}` : label} data-testid={testId}>
      <option value="">{na ? '—' : `Tous`}</option>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </Select360>
    {na && <small className="flt-na sr-only" data-testid={`${testId}-na`}>{NA_TITLE}</small>}
  </label>
);

/**
 * @param na          filtres inapplicables sur la page (grisés avec explication) : 'period' | 'pdv' | 'service' | 'compare'
 * @param periodKinds types de période proposés (ex. ['day'] : Tâches se consultent jour par jour)
 */
export const FiltersBar = ({ na = [], testPrefix = 'filters', onMomentsSaved, closure, periodKinds }) => {
  const { tenant } = useTenant();
  const [params, setParams] = useSearchParams();
  const { moments, lieux, reload } = useMoments();
  const [editing, setEditing] = useState(false);
  useFiltersPersistence();
  const { pdv, service } = useAxes();
  const lieuOpts = useMemo(() => { const used = new Set(moments.map((m) => m.lieu_id).filter(Boolean)); return lieux.filter((l) => used.has(l.lieu_id) || !used.size).map((l) => [l.lieu_id, l.name]); }, [moments, lieux]);
  const serviceOpts = useMemo(() => [...new Set(moments.map((m) => m.service).filter(Boolean))].map((s) => [s, s]), [moments]);
  const set = (k, v) => { const p = new URLSearchParams(params); p.delete('moment'); if (v) p.set(k, v); else p.delete(k); setParams(p); };
  const saved = () => { setEditing(false); reload(); window.dispatchEvent(new Event('moments:updated')); onMomentsSaved?.(); };
  const mobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const period = usePeriodParams(tenant?.business_day_start);
  // `?moments=1` (page Liaisons, alertes) ouvre directement le paramétrage des points de vente et moments
  useEffect(() => { if (new URLSearchParams(window.location.search).get('moments') === '1') setEditing(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!open) return undefined; const k = (e) => e.key === 'Escape' && setOpen(false); document.addEventListener('keydown', k); return () => document.removeEventListener('keydown', k); }, [open]);
  const bar = <section className={`flt-bar ${na.includes('compare') ? 'no-compare' : ''}`} aria-label="Filtres de la page" data-testid={testPrefix}>
    <PeriodNav businessDayStart={tenant?.business_day_start} testPrefix={`${testPrefix}-period`} closure={closure} kinds={periodKinds} na={na.includes('period')} />
    <div className={`flt-axes ${na.includes('pdv') && na.includes('service') ? 'is-all-na' : ''}`} data-testid={`${testPrefix}-axes`}>
      <Select label="Point de vente" value={pdv} onChange={(v) => set('pdv', v)} options={lieuOpts} na={na.includes('pdv')} testId={`${testPrefix}-pdv`} />
      <Select label="Moment" value={service} onChange={(v) => set('service', v)} options={serviceOpts} na={na.includes('service')} testId={`${testPrefix}-service`} />
      <button type="button" className="fin-moments-gear flt-gear" onClick={() => setEditing(true)} title="Paramétrer les points de vente et les moments de service (noms, horaires, caisse, lieu). Vous revenez ensuite sur cette page." data-testid={`${testPrefix}-moments-settings`}><Settings2 size={14} /> Paramétrer les moments</button>
    </div>
    {editing && <MomentsDialog moments={moments} onClose={() => setEditing(false)} onSaved={saved} />}
  </section>;
  if (!mobile) return bar;
  // Téléphone (app Happn) : une seule pilule « Filtres » qui résume le périmètre et ouvre une feuille en bas
  const parts = [!na.includes('period') && period.label, !na.includes('pdv') && (lieuOpts.find(([v]) => v === pdv)?.[1] || null), !na.includes('service') && (service || null)].filter(Boolean);
  const nActive = (pdv && !na.includes('pdv') ? 1 : 0) + (service && !na.includes('service') ? 1 : 0);
  return <>
    <button type="button" className="flt-pill" data-keep-icon="1" data-keepicon="1" onClick={() => { haptic.tap(); setOpen(true); }} aria-haspopup="dialog" aria-expanded={open} data-testid={`${testPrefix}-pill`}>
      <SlidersHorizontal size={16} aria-hidden />
      <span className="flt-pill-txt"><b>Filtres</b>{parts.length ? <span className="flt-pill-sum"> · {parts.join(' · ')}</span> : null}</span>
      {nActive > 0 && <b className="flt-pill-n" data-testid={`${testPrefix}-pill-count`}>{nActive}</b>}
    </button>
    {open && createPortal(<div className="flt-sheet-wrap" role="dialog" aria-modal="true" aria-label="Filtres" data-testid={`${testPrefix}-sheet`}>
      <button type="button" className="flt-sheet-veil" aria-label="Fermer les filtres" onClick={() => setOpen(false)} data-testid={`${testPrefix}-sheet-veil`} />
      <div className="flt-sheet">
        <span className="flt-sheet-handle" aria-hidden />
        <div className="flt-sheet-head"><h2 className="flt-sheet-title">Filtres<span className="dot">.</span></h2><button type="button" className="flt-sheet-x" onClick={() => setOpen(false)} aria-label="Fermer" data-testid={`${testPrefix}-sheet-close`}><X size={18} /></button></div>
        {bar}
        <button type="button" className="flt-sheet-ok" onClick={() => { haptic.tap(); setOpen(false); }} data-testid={`${testPrefix}-sheet-ok`}>Voir les résultats</button>
      </div>
    </div>, document.body)}
  </>;
};
